import { createSelector } from 'reselect';
import { isNil } from 'lodash';
import { modules } from '@veritone/glc-redux';
import {
  AssetFileType,
  CaseMediaState,
  FolderPermissionFlags,
  FolderStateType,
  namespace,
} from '@redact-modules/casemedia/store';
import {
  FolderId,
  TDOId,
  TreeObjectId,
} from '@common-modules/universal/models/Brands';
import { selectEnableFolderViewTiles } from '@common/user-onboarding/selectors/organization';
import { isPdfFile } from '@helpers/mediaFile';
import { mapOutputMediaFiles } from '@redact-modules/casemedia/reducers';
import {
  UNASSIGNED_MEDIA_FOLDER_NAME,
  buildUnassignedFolder,
  isUnassignedFolderId,
  mapUnassignedFile,
} from '@redact-modules/casemedia/unassigned';

const {
  user: { selectUser },
} = modules;

export const defaultLimit = 30;
export const defaultOffset = 0;

/**
 * Shared empty result for `selectEditorRequestDocuments`, so every "no sibling
 * documents" answer is the SAME reference and consumers don't re-render on
 * unrelated casemedia activity.
 */
const EMPTY_EDITOR_REQUEST_DOCUMENTS: AssetFileType[] = [];

export const selectStore = (state: { [namespace]: CaseMediaState }) =>
  state[namespace];

export const selectRootFolder = (state: { [namespace]: CaseMediaState }) =>
  state[namespace].rootFolder;

// ---------------------------------------------------------------------------
// VE-26943 — Unassigned Media as a folder.
//
// The unassigned list is DERIVED into folder shape rather than stored that way:
// `LegacyMedias` holds the redact-state SDOs (identity + status) and
// `legacyTdoDetails` their enriched TDOs, and this pair produces the same
// `AssetFileType` rows a request's files have. Selection stores only a stub
// carrying the sentinel id (see setSelectedFolderRequest); the substitution in
// selectSelectedFolders / selectSelectedVirtualFolder below swaps in the fresh
// derivation on read, so a page arriving mid-browse shows up without anyone
// having to re-dispatch the selection.
//
// Declared here, above the selection selectors that consume them, because
// createSelector evaluates its inputs eagerly at module load.
// ---------------------------------------------------------------------------

// Narrow inputs, not `selectStore`: this maps every unassigned file (and, via
// selectUnassignedFolder, flattens all their output assets). Keyed on the whole
// slice it would redo that work on EVERY casemedia action — a filter keystroke,
// a menu open — for libraries that can run to thousands of files.
const selectLegacyMediasSlice = (state: { [namespace]: CaseMediaState }) =>
  selectStore(state)?.LegacyMedias;
const selectLegacyTdoDetailsSlice = (state: { [namespace]: CaseMediaState }) =>
  selectStore(state)?.legacyTdoDetails;
const selectLegacyTdoDetailAttempted = (state: {
  [namespace]: CaseMediaState;
}) => selectStore(state)?.legacyTdoDetailAttempted;

export const selectUnassignedFiles = createSelector(
  selectLegacyMediasSlice,
  selectLegacyTdoDetailsSlice,
  selectLegacyTdoDetailAttempted,
  (legacyMedias, tdoDetails, attempted) => {
    // Withhold a row until its TDO detail lands. The SDO alone carries no file
    // name (it seeds `tdoName` with the tdoId) and no job/task records, so an
    // unenriched row renders as a raw id wearing a red "error" badge — which is
    // exactly what the second phase is about to correct. The folder shows its
    // loading spinner instead, and rows appear correct the first time.
    //
    // A row whose enrichment was ATTEMPTED and produced nothing is released
    // anyway, degraded: the SDOs are the record that these files exist, so
    // hiding them for good would be worse. Tracked per tdo rather than with one
    // failed/not-failed flag, which could not say which rows were affected.
    const details = tdoDetails ?? {};
    const tried = attempted ?? {};
    return (legacyMedias || [])
      .filter(
        (legacy) =>
          !!details[`id_${legacy.tdoId}`] || !!tried[`id_${legacy.tdoId}`]
      )
      .map((legacy) =>
        mapUnassignedFile(legacy, details[`id_${legacy.tdoId}`])
      );
  }
);

// Narrow inputs on purpose: depending on `selectStore` would rebuild the folder
// object on EVERY casemedia action (a filter toggle, a menu open), and via the
// substitution below that would hand every consumer of selectSelectedFolders a
// new array each time.
export const selectUnassignedFolder = createSelector(
  selectUnassignedFiles,
  (s: { [namespace]: CaseMediaState }) =>
    !!selectStore(s)?.fetchMoreLegacyMediasAvailable,
  (s: { [namespace]: CaseMediaState }) =>
    !!selectStore(s)?.isFetchingLegacyMedias,
  (files, fetchMoreAvailable, fetchingFiles) =>
    buildUnassignedFolder({
      files,
      // The download paths read outputs off the FOLDER, not the file, so the
      // synthetic folder has to carry them too — built from the same asset
      // records a request's files arrive with (VE-26943).
      outputFiles: mapOutputMediaFiles(files),
      // Untranslated fallback — components render the `legacyMediasTitle`
      // message; selectors have no intl context.
      name: UNASSIGNED_MEDIA_FOLDER_NAME,
      fetchMoreAvailable,
      fetchingFiles,
    })
);

/** Is the synthetic Unassigned Media folder the current selection? */
export const selectIsUnassignedSelected = createSelector(selectStore, (s) =>
  (s?.selectedFolders || []).some((folder) => isUnassignedFolderId(folder?.id))
);

/**
 * Is the docked Unassigned Media panel actually on screen? The dock and the
 * selection are two presentations of the same folder, so the dock yields while
 * the folder is open — one definition, shared by the layout that renders it and
 * the header button that toggles it, so the button can never claim a state the
 * user isn't looking at.
 */
export const selectIsDockVisible = createSelector(
  selectStore,
  selectIsUnassignedSelected,
  (s, isUnassignedSelected) => !!s?.isShowLegacyMedias && !isUnassignedSelected
);

export const selectRootFolderId = (state: { [namespace]: CaseMediaState }) =>
  state[namespace]?.rootFolderId || '';

export const selectIsFetchingRootFolders = createSelector(
  selectStore,
  (s) => s.fetchingRootFolders
);

// Keyed on the selection slice, NOT the whole store: `selectStore` changes
// identity on every casemedia action, so taking it as an input re-ran the
// substitution — and handed every consumer a new array — on each batch-progress
// tick, filter keystroke and menu open while this folder was open.
const selectSelectedFoldersSlice = (state: { [namespace]: CaseMediaState }) =>
  selectStore(state)?.selectedFolders;

export const selectSelectedFolders = createSelector(
  selectSelectedFoldersSlice,
  selectUnassignedFolder,
  (selectedFolders, unassignedFolder) => {
    const folders = selectedFolders || [];
    // Return the SAME array reference when no unassigned folder is selected.
    // Mapping unconditionally would produce a new array on every recompute and
    // re-render every consumer — for orgs that never touch this folder too.
    return folders.some((folder) => isUnassignedFolderId(folder?.id))
      ? folders.map((folder) =>
          isUnassignedFolderId(folder?.id) ? unassignedFolder : folder
        )
      : folders;
  }
);

export const selectSelectedFolderIds = createSelector(
  selectStore,
  (s) => s?.selectedFolders.map((f) => f.id) || []
);
const selectSelectedVirtualFolderSlice = (state: {
  [namespace]: CaseMediaState;
}) => selectStore(state)?.selectedVirtualFolder;

export const selectSelectedVirtualFolder = createSelector(
  selectSelectedVirtualFolderSlice,
  selectUnassignedFolder,
  (selectedVirtualFolder, unassignedFolder) => {
    const virtualFolder = selectedVirtualFolder || {};
    // Same reference-stability rule as selectSelectedFolders above.
    return isUnassignedFolderId(virtualFolder.parent?.id)
      ? { ...virtualFolder, parent: unassignedFolder }
      : virtualFolder;
  }
);

/**
 * VE-28429: when this request's contents last landed, or `undefined` if they
 * never have this session.
 *
 * VE-30022: a record, not a gate — the editor return's reuse is decided by
 * `selectFolderViewTrip`. Kept as the honest "when did this come off the
 * server" for a background refresh to run from.
 */
export const selectCaseContentsLoadedAt =
  (folderId: FolderId | undefined) =>
  (state: { [namespace]: CaseMediaState }): number | undefined =>
    folderId ? selectStore(state)?.caseContentsLoadedAt?.[folderId] : undefined;

/** When this folder's subfolder listing last landed (page one), if ever. */
export const selectSubFoldersLoadedAt =
  (folderId: FolderId | undefined) =>
  (state: { [namespace]: CaseMediaState }): number | undefined =>
    folderId ? selectStore(state)?.subFoldersLoadedAt?.[folderId] : undefined;

export const selectRootFoldersLimit = createSelector(
  selectStore,
  (s) => s.rootFolder?.fetchSubFolderLimit || defaultLimit
);
export const selectRootFoldersOffset = createSelector(
  selectStore,
  (s) => s.rootFolder?.fetchSubFolderOffset || defaultOffset
);

export const selectDefaultLimit = createSelector(
  selectStore,
  (s) => s?.defaultFetchMoreLimit || defaultLimit
);

export const selectDefaultOffset = createSelector(
  selectStore,
  (s) => s?.defaultFetchMoreOffset || defaultOffset
);

export const selectFetchMoreFolderLimit = (selectedId: string) =>
  createSelector(selectStore, selectSelectedFolders, (s, folders) => {
    const selectedFolder = folders.filter(
      (folder: FolderStateType) => folder.id === selectedId
    )[0];
    if (!selectedFolder) {
      return s.defaultFetchMoreLimit;
    }
    return selectedFolder.fetchSubFolderLimit
      ? selectedFolder.fetchSubFolderLimit
      : s.defaultFetchMoreLimit;
  });

export const selectFetchMoreFolderOffset = (selectedId: string) =>
  createSelector(selectStore, selectSelectedFolders, (s, folders) => {
    const selectedFolder = folders.filter(
      (folder: FolderStateType) => folder.id === selectedId
    )[0];

    return selectedFolder?.fetchSubFolderOffset
      ? selectedFolder.fetchSubFolderOffset
      : s.defaultFetchMoreOffset;
  });

export const selectIsNavToCaseMedia = (state: {
  [namespace]: CaseMediaState;
}): boolean => state[namespace].isNavToCaseMedia || false;

export const selectNavToId = (state: { [namespace]: CaseMediaState }) =>
  state[namespace].navToId || undefined;

export const selectIsNavToCase = (state: {
  [namespace]: CaseMediaState;
}): boolean => state[namespace].isNavToCase || false;

// VE-25873 Unit 3 — the parent request a pending editor session should land
// back on at exit (null when nothing is pending).
export const selectEditorReturnTarget = (state: {
  [namespace]: CaseMediaState;
}): CaseMediaState['editorReturnTarget'] =>
  state[namespace].editorReturnTarget ?? null;

// VE-30022 — the current trip away from the folder view: when it was left for
// an editor and which columns it was showing. Null before the first editor of
// the session, and after a tree reset.
export const selectFolderViewTrip = (state: {
  [namespace]: CaseMediaState;
}): CaseMediaState['folderViewTrip'] => state[namespace].folderViewTrip ?? null;

export const selectSearchText = createSelector(
  selectStore,
  (s) => s.searchText
);

export const selectSearchResults = createSelector(
  selectStore,
  (s) => s.searchResults
);

export const selectSearchDefaultOffset = createSelector(
  selectStore,
  (s) => s.searchDefaultOffset
);

export const selectSearchDefaultFolderLimit = createSelector(
  selectStore,
  (s) => s.searchDefaultFolderLimit
);

export const selectSearchDefaultFileLimit = createSelector(
  selectStore,
  (s) => s.searchDefaultFileLimit
);

export const selectSearchMoreFilesOffset = createSelector(
  selectStore,
  (s) => s.searchMoreFilesOffset
);

export const selectSearchMoreFoldersOffset = createSelector(
  selectStore,
  (s) => s.searchMoreFoldersOffset
);

export const selectSearchMoreFilesAvailable = createSelector(
  selectStore,
  (s) => s.searchMoreFilesAvailable
);

export const selectSearchMoreFoldersAvailable = createSelector(
  selectStore,
  (s) => s.searchMoreFoldersAvailable
);

export const selectIsSearchMoreResults = createSelector(
  selectStore,
  (s) => s.isSearchMoreResults
);

export const selectIsSearchInProgress = createSelector(
  selectStore,
  (s) => s.isSearchInProgress
);

export const selectSearchScopeFolderId = createSelector(
  selectStore,
  (s) => s.searchScopeFolderId
);

export const selectSearchScopeTreeObjectId = (state: {
  [namespace]: CaseMediaState;
}): TreeObjectId | null => selectStore(state).searchScopeTreeObjectId;

export const selectSearchScopeFolderName = createSelector(
  selectStore,
  (s) => s.searchScopeFolderName
);

export const selectSearchScopeIsCase = createSelector(
  selectStore,
  (s) => s.searchScopeIsCase
);

export const selectSearchBarScopeMode = createSelector(
  selectStore,
  (s) => s.searchBarScopeMode ?? 'everything'
);

export const selectIsSearchDialogOpen = createSelector(
  selectStore,
  (s) => s.isSearchDialogOpen
);

export const selectSearchResultsCache = createSelector(
  selectStore,
  (s) => s.searchResultsCache
);

// Cache key for a search: query + scope mode + (for scoped searches) the
// folder being searched. 'everything' is folder-independent.
export const buildSearchCacheKey = (
  query: string,
  mode: 'selectedFolder' | 'everything',
  folderId?: string | null
): string =>
  `${query.trim()}::${mode}::${mode === 'selectedFolder' ? (folderId ?? '') : ''}`;

export const selectCurrentSearchScopeFolder = createSelector(
  selectSelectedFolders,
  selectSelectedVirtualFolder,
  selectRootFolder,
  (selectedFolders, selectedVirtualFolder, rootFolder) => {
    if (
      selectedVirtualFolder?.parent?.id &&
      selectedVirtualFolder?.parent?.name
    ) {
      return {
        folderId: selectedVirtualFolder.parent.id,
        folderName: selectedVirtualFolder.parent.name,
        treeObjectId: selectedVirtualFolder.parent.treeObjectId,
        isCase: true,
        isRoot: false,
      };
    }
    const lastFolder = selectedFolders[selectedFolders.length - 1];
    if (lastFolder?.id && lastFolder?.name) {
      return {
        folderId: lastFolder.id,
        folderName: lastFolder.name,
        treeObjectId: lastFolder.treeObjectId,
        isCase: (lastFolder.contentTemplates?.length ?? 0) > 0,
        isRoot: false,
      };
    }
    if (rootFolder?.id && rootFolder?.name) {
      return {
        folderId: rootFolder.id,
        folderName: rootFolder.name,
        treeObjectId: rootFolder.treeObjectId,
        isCase: (rootFolder.contentTemplates?.length ?? 0) > 0,
        isRoot: true,
      };
    }
    return null;
  }
);

export const selectIsFetchingLegacyMedias = createSelector(
  selectStore,
  (s) => s.isFetchingLegacyMedias
);

export const selectIsShowLegacyMedias = createSelector(
  selectStore,
  (s) => s.isShowLegacyMedias
);

export const selectLegacyMedias = createSelector(
  selectStore,
  (s) => s.LegacyMedias
);

export const selectFetchMoreLegacyMediasAvailable = createSelector(
  selectStore,
  (s) => s.fetchMoreLegacyMediasAvailable
);

export const selectFetchMoreLegacyMediasOffset = createSelector(
  selectStore,
  (s) => s.fetchMoreLegacyMediasOffset
);

/** Has the unassigned-list probe answered yet (either way)? */
export const selectHasFetchedLegacyMedias = createSelector(
  selectStore,
  (s) => !!s.hasFetchedLegacyMedias
);

/** True once the mount probe has shown the org has unassigned files at all. */
export const selectHasUnassignedFiles = createSelector(
  selectLegacyMedias,
  (legacyMedias) => (legacyMedias?.length || 0) > 0
);

export const selectAllExpandedFolders = createSelector(
  selectStore,
  (s) => s.allExpandedFolders || []
);

export const selectSourceFolderToMove = createSelector(
  selectStore,
  (s) => s.sourceFolderToMove
);
export const selectMoveFromFolder = createSelector(
  selectStore,
  (s) => s.moveFromFolder
);

export const selectSourceFileToMove = createSelector(
  selectStore,
  (s) => s.sourceFileToMove
);

export const selectSelectedRequestToIngest = createSelector(
  selectStore,
  (s) => s.selectedRequestToIngest
);

export const selectSourceInputFileToMove = createSelector(
  selectStore,
  (s) => s.sourceInputFileToMove
);

export const selectSelectedTdoFile = (tdoId: TDOId) =>
  createSelector(
    selectSelectedVirtualFolder,
    (folder) =>
      (folder?.parent?.caseFiles?.inputFiles || []).filter(
        (f) => f.id === tdoId
      )?.[0] as AssetFileType
  );

export const selectFilters = createSelector(selectStore, (s) => s.filters);

export const selectFileViewMode = createSelector(
  selectStore,
  selectIsUnassignedSelected,
  // Classic is the safe default — must match readStoredFileViewMode's
  // fallback (Details/Grid only ever activate from an explicit toggle
  // choice).
  //
  // VE-26943: Unassigned Media answers from its own preference, which defaults
  // to Grid instead. Resolving it HERE means every downstream consumer
  // (selectIsPanelViewMode, selectIsDetailsViewMode, the toggle's own value)
  // follows automatically.
  (s, isUnassignedSelected) =>
    isUnassignedSelected
      ? s.unassignedFileViewMode || 'grid'
      : s.fileViewMode || 'classic'
);

/**
 * VE-26943: is the folder sidebar collapsed ("wide screen mode")? Only
 * meaningful in the two-panel Grid/Details layouts. Answers from Unassigned
 * Media's own preference when that folder is open, and from the shared one
 * otherwise — they default opposite ways.
 */
export const selectIsSidebarCollapsed = createSelector(
  selectStore,
  selectIsUnassignedSelected,
  (s, isUnassignedSelected) =>
    isUnassignedSelected
      ? s.isUnassignedSidebarCollapsed !== false
      : s.isPanelSidebarCollapsed === true
);

// Single definition of "a two-panel view mode is active" (Details or Grid —
// everything except Classic's multi-column cascade). Shared by CaseFolders
// (column layout) and FolderColumn (header shape) so the two can't drift.
// VE-26943: Unassigned Media gets the two-panel modes regardless of the
// folderViewTiles flag — the folder exists to land migrated gallery customers
// somewhere familiar, and Grid is that. Everything else stays flag-gated until
// the flag is retired.
export const selectIsPanelViewMode = createSelector(
  selectFileViewMode,
  selectEnableFolderViewTiles,
  selectIsUnassignedSelected,
  (fileViewMode, enableFolderViewTiles, isUnassignedSelected) =>
    (enableFolderViewTiles || isUnassignedSelected) &&
    fileViewMode !== 'classic'
);

export const selectIsDetailsViewMode = createSelector(
  selectFileViewMode,
  selectEnableFolderViewTiles,
  selectIsUnassignedSelected,
  (fileViewMode, enableFolderViewTiles, isUnassignedSelected) =>
    (enableFolderViewTiles || isUnassignedSelected) &&
    fileViewMode === 'details'
);

export const selectFolderTreeFetchState = createSelector(
  selectStore,
  (s) => s.folderTreeFetchState || 'idle'
);

export const selectLastActiveFolderViewFile = createSelector(
  selectStore,
  (s) => s.lastActiveFolderViewFile
);

export const selectOpenedFolderViewTdoIds = createSelector(
  selectStore,
  (s) => s.openedFolderViewTdoIds || []
);

export const selectLastOpenedFolderViewFileByCase = createSelector(
  selectStore,
  (s) => s.lastOpenedFolderViewFileByCase || {}
);

// VE-25098: folder-view multi-file selection.
export const selectMultiSelectedFiles = createSelector(
  selectStore,
  (s) => s.multiSelectedFiles || []
);

// Derived boolean so per-tile subscribers don't re-render on every toggle
// (the array is replaced per selection change; this only flips empty<->not).
export const selectHasMultiSelectedFiles = createSelector(
  selectMultiSelectedFiles,
  (files) => files.length > 0
);

export const selectMultiSelectCaseId = createSelector(
  selectStore,
  (s) => s.multiSelectCaseId
);

export const selectMultiSelectAnchorId = createSelector(
  selectStore,
  (s) => s.multiSelectAnchorId
);

export const selectPendingRemovalFileIds = createSelector(
  selectStore,
  (s) => s.pendingRemovalFileIds || []
);

export const selectPendingTrashFolderIds = createSelector(
  selectStore,
  (s) => s.pendingTrashFolderIds || []
);

// Optional-chained on purpose: this is the one casemedia selector read from a component
// mounted above the router (BatchOperationUnloadGuard), so it can run in a build whose
// store has no casemedia slice. Everything else here runs only inside redact pages.
export const selectBatchOperation = createSelector(
  selectStore,
  (s) => s?.batchOperation
);

export const selectSelectedOutputFiles = createSelector(
  selectSelectedVirtualFolder,
  (folder) => folder?.parent?.caseFiles?.outputFiles || []
);

export const selectIsOlpEnabled = (state: unknown): boolean => {
  try {
    // glc-redux's User types don't declare `organization.kvp.features` — the
    // field is present at runtime for OLP orgs but not in the .d.ts — so read
    // it through a locally-typed view of the user rather than the shipped type.
    const user = selectUser(state as never) as unknown as
      | {
          organization?: {
            kvp?: { features?: { enableRBACFeature?: string } };
          };
        }
      | undefined;
    return user?.organization?.kvp?.features?.enableRBACFeature === 'enabled';
  } catch {
    // The glc-redux user slice can be absent (before auth hydrates, or in tests
    // with a partial store), which makes selectUser throw. Treat that as OLP
    // disabled so the folder view degrades to its non-gated behaviour.
    return false;
  }
};

export const selectFolderPermissions = createSelector(
  selectStore,
  (s) => s.folderPermissions || {}
);

// The single source of truth for the OLP folder-edit gate — every consumer must
// come through here rather than reading folderPermissions directly.
//
// Fail-CLOSED: a folder is editable only once a check has CONFIRMED it. An
// absent entry means "not confirmed" — never checked, still in flight, or the
// check failed — and reads as non-editable. That closes the window where the
// menu rendered fully enabled while the check was still in flight (the fetch is
// dispatched at menu-open time, so that window is exactly when the user is
// reading the menu). A failed check deliberately caches nothing (see
// fetchFolderPermissions), so an absent entry is self-healing: the next menu
// open re-runs the check instead of being stuck for the session.
//
// Non-OLP orgs are ungated, and the short-circuit for that lives HERE on
// purpose — returning before folderPermissions is read makes `?? false`
// unreachable off-OLP, so "a non-OLP org does no OLP work" is structural rather
// than a convention every caller has to remember.
const selectFolderPermission = (
  state: { [namespace]: CaseMediaState },
  folderId: FolderId | null | undefined,
  flag: keyof FolderPermissionFlags
): boolean => {
  if (isNil(folderId) || !selectIsOlpEnabled(state)) {
    return true;
  }
  return selectStore(state).folderPermissions?.[folderId]?.[flag] ?? false;
};

// Rename — `updateFolder`, AIWARE_FOLDER_UPDATE. Also the gate the folder-view
// UX applies to actions that write INTO a folder (add folder / add request /
// upload media / change a file's status). Those mutations are authorized
// org-wide rather than per-folder on the server, so gating them here is a
// deliberate UX policy: a folder you can only view shouldn't offer to be
// written into. The server remains the security boundary.
export const selectCanEditFolder = (
  state: { [namespace]: CaseMediaState },
  folderId?: FolderId | null
): boolean => selectFolderPermission(state, folderId, 'canEdit');

// True only once a check has CONFIRMED the folder is not editable. Deliberately
// NOT `!selectCanEditFolder(...)`: that gate reads an unchecked folder as denied,
// which is right for disabling a control (briefly inert, harmless) but wrong for
// a badge, which would then assert "view only" on every folder whose verdict is
// merely still in flight — including ones the user owns. Reading presence in the
// map instead keeps the badge silent until there is something real to say.
export const selectIsFolderConfirmedViewOnly = (
  state: { [namespace]: CaseMediaState },
  folderId?: FolderId | null
): boolean => {
  if (isNil(folderId) || !selectIsOlpEnabled(state)) {
    return false;
  }
  const verdict = selectStore(state).folderPermissions?.[folderId];
  return verdict !== undefined && !verdict.canEdit;
};

// Move — `moveFolder`, AIWARE_FOLDER_FILE. A SOFT delete needs this too (moving
// to Trash IS a moveFolder), but never on its own — see selectCanDeleteFolder.
export const selectCanMoveFolder = (
  state: { [namespace]: CaseMediaState },
  folderId?: FolderId | null
): boolean => selectFolderPermission(state, folderId, 'canMove');

// Delete — `deleteFolder`, AIWARE_FOLDER_DELETE. Required for BOTH delete
// branches: an org with trashRetentionDays > 0 only moves to Trash right now, but
// retention expiry purges the folder later, so starting that still amounts to
// deleting it. Callers on the soft path must require canMove as well (the move
// the branch actually performs); this selector deliberately answers only for the
// delete permission so the two conditions stay visible at the call site.
export const selectCanDeleteFolder = (
  state: { [namespace]: CaseMediaState },
  folderId?: FolderId | null
): boolean => selectFolderPermission(state, folderId, 'canDelete');

/**
 * VE-24947 (U2-FR1/FR3) — the sibling documents of the file currently open in
 * the document editor: every PDF in its parent request.
 *
 * Reads only what an editor session already has. `resolveEditorReturnTarget`
 * records the parent request as `editorReturnTarget` at route init, and
 * `warmEditorReturnTargetTree` loads that request's column chain and contents
 * into `selectedFolders` while the user is still editing (VE-25873) — so the
 * Documents panel needs no fetch of its own and no new query.
 *
 * Returns an EMPTY array, never a partial one, when the request isn't resolved
 * or its contents haven't landed yet. The panel reads emptiness as "nothing to
 * switch to" and hides itself, which is also the correct answer for a file
 * opened without case context and for a legacy-folder-model org.
 *
 * PDFs only. A non-PDF sibling belongs to the media editor, which is a
 * different route and a different editor — offering it here would promise an
 * in-place switch this panel cannot make.
 */
/**
 * The request folder the editor came from, once its contents have landed.
 *
 * Match on both keys: the recorded target is whatever the URL carried, and a
 * request is addressable by either its folder id or its treeObjectId — the same
 * two-key test `isTargetSelected` applies in caseDiscovery. Shared by the two
 * selectors below so "which folder is this" cannot answer differently for the
 * list and for the question of whether the list has arrived.
 */
const findEditorRequestFolder = (
  target: CaseMediaState['editorReturnTarget'],
  selectedFolders: ReturnType<typeof selectSelectedFolders>
) =>
  target?.folderId
    ? selectedFolders.find(
        (f) =>
          f.id === target.folderId ||
          (f.treeObjectId as string | undefined) === target.folderId
      )
    : undefined;

/**
 * VE-24947 — is the sibling list still on its way?
 *
 * `selectEditorRequestDocuments` returns an empty array for two situations it
 * cannot tell apart: a request that genuinely holds no other PDFs, and one
 * whose contents `warmEditorReturnTargetTree` has not delivered yet. They are
 * distinguishable HERE, one level down — an absent `caseFiles.inputFiles` means
 * nothing has landed, where a present one that yields no PDFs is an answer —
 * so consumers that want to say "loading" rather than "empty" can.
 *
 * False when there is no case context at all (a file opened by direct link, a
 * legacy-folder org): nothing is coming, so nothing is pending. NOT false-able
 * on fetch failure, though — a warm that fails leaves the contents absent and
 * this true indefinitely, so a caller showing a spinner on it must bound the
 * wait rather than trust it to end.
 */
export const selectEditorRequestDocumentsPending = createSelector(
  selectEditorReturnTarget,
  selectSelectedFolders,
  (target, selectedFolders): boolean => {
    if (!target?.folderId) {
      return false;
    }
    return !findEditorRequestFolder(target, selectedFolders)?.caseFiles
      ?.inputFiles;
  }
);

export const selectEditorRequestDocuments = createSelector(
  selectEditorReturnTarget,
  selectSelectedFolders,
  (target, selectedFolders): AssetFileType[] => {
    if (!target?.folderId) {
      return EMPTY_EDITOR_REQUEST_DOCUMENTS;
    }
    const folder = findEditorRequestFolder(target, selectedFolders);
    const inputFiles = folder?.caseFiles?.inputFiles;
    if (!inputFiles?.length) {
      return EMPTY_EDITOR_REQUEST_DOCUMENTS;
    }
    // Classify through the shared resolver, NOT the flat `contentType` field.
    // Case-file rows carry their MIME on `primaryAsset.contentType` (see
    // `mapMediaFiles` — "primaryAsset.contentType populates the proper icon /
    // type once ingest completes"), and the flat field is a URL-derived hint
    // set at TDO create that is frequently absent. Filtering on it dropped
    // every row. `isPdfFile` checks primaryAsset first and falls back through
    // the veritoneFile shapes, which is why the folder view, the media drawer
    // and the grid all agree on what a PDF is.
    const documents = inputFiles.filter((file) => isPdfFile(file));
    // Preserve the shared-empty reference so a request of only media files
    // doesn't hand consumers a fresh [] on every recompute.
    return documents.length ? documents : EMPTY_EDITOR_REQUEST_DOCUMENTS;
  }
);
